import React, { useEffect, useMemo } from "react";
import {
  Button,
  Input,
  Modal,
  Pagination,
  SelectMenu,
  Text,
} from "@zorni/components-lib";
import { useTranslation } from "react-i18next";

import Preloader from "../../../../common/preLoader/PreLoader";
import { USER_STATUS } from "../../../common/model/enum";

import UserTable from "./components/UserTable";
import AddUser from "./components/AddUser";
import PreviewUser from "./components/PreviewUser";
import RelaunchUser from "./components/RelaunchUser";
import { useManageUsers } from "./hooks/useManageUsers";
import { useUserFormValidation } from "./hooks/useUserFormValidation";
import { useAdmin } from "../../../hooks/useAdmin";

import styles from "./ManageUsers.module.css";

const ManageUsers = () => {
  const { t } = useTranslation();
  const { hasPARole, hasPaRole } = useAdmin();

  const {
    users,
    usersLoading,
    currentPage,
    setCurrentPage,
    nbPages,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    selectedEstablishment,
    setSelectedEstablishment,
    isAddModalOpen,
    isEditModalOpen,
    isRelaunchModalOpen,
    selectedUser,
    addForm,
    addRoleOptions,
    addEstablishmentOptions,
    draftRole,
    editRoles,
    editRoleOptions,
    editEstablishmentOptions,
    modalLoading,
    openAddModal,
    openEditModal,
    openRelaunchModal,
    closeModal,
    onAddFormChange,
    onDraftRoleChange,
    onRoleUpdate,
    onRoleRemove,
    onDraftRoleAdd,
    onSubmitAdd,
    onSubmitEdit,
    onSubmitRelaunch,
  } = useManageUsers();

  const canAccessPage = Boolean(hasPaRole ?? hasPARole);
  const {
    errors: addUserErrors,
    validateForm: validateAddUserForm,
    clearErrors: clearAddUserErrors,
  } = useUserFormValidation(addForm);

  const statusOptions = [
    { value: "all", label: "Tous" },
    { value: USER_STATUS.ACTIVATION_IN_PROGRESS, label: "Inactifs" },
    { value: USER_STATUS.ACTIVATED, label: "Actifs" },
    { value: USER_STATUS.MISSING_INFORMATIONS, label: "Incomplets" },
  ];

  const establishmentFilterOptions = useMemo(
    () => [
      {
        index: 0,
        value: "all",
        text: "Tous les établissements",
      },
      ...addEstablishmentOptions.map((option, index) => ({
        index: index + 1,
        value: option.value,
        text: option.text,
      })),
    ],
    [addEstablishmentOptions]
  );

  useEffect(() => {
    if (!isAddModalOpen) {
      clearAddUserErrors();
    }
  }, [isAddModalOpen, clearAddUserErrors]);

  if (!canAccessPage) {
    return null;
  }

  return (
    <div className={styles.page}>
      <div className={styles.headerCard}>
        <div>
          <Text as="h3" variant="heading" size="lg" weight="semibold">
            Equipe & Accès
          </Text>
          <Text size="sm" color="subtle">
            Administration des rôles
          </Text>
        </div>

        <Button
          label={t("manageUsers.modal.addUser")}
          variant="primary"
          iconName="iconUserPlus"
          iconPosition="left"
          onClick={openAddModal}
        />
      </div>

      <div className={styles.card}>

          <div className={styles.filterBar}>
            <div className={styles.searchWrapper}>
              <Input
                inputSize="small"
                iconLeft="iconSearch"
                placeholder="Rechercher par email ou nom..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div className={styles.filtersRight}>
              <SelectMenu t={t}
                inputSize="small"
                options={establishmentFilterOptions}
                value={selectedEstablishment}
                onChange={(value, option) => setSelectedEstablishment(option.value)}
              />

              <div className={styles.statusToggle}>
                {statusOptions.map((option) => (
                  <Button
                    key={option.value}
                    size="small"
                    variant={statusFilter === option.value ? "primary" : "secondary"}
                    label={option.label}
                    onClick={() => setStatusFilter(option.value)}
                  />
                ))}
              </div>
            </div>
          </div>

        {usersLoading ? (
          <div className={styles.loading}>
            <Preloader />
          </div>
        ) : (
          <UserTable
            users={users}
            onEdit={openEditModal}
            onRelaunch={openRelaunchModal}
          />
        )}

        <Pagination
          totalPages={nbPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <Modal t={t}
        isOpen={isAddModalOpen}
        onClose={closeModal}
        title={t("manageUsers.modal.addUser")}
        footer={
          <div className={styles.modalFooter}>
            <Button
              label={t("commons.buttons.cancel")}
              variant="secondary"
              onClick={closeModal}
            />
            <Button
              label={t("commons.buttons.submit")}
              onClick={() => {
                if (!validateAddUserForm()) return;
                onSubmitAdd();
              }}
              disabled={modalLoading}
            />
          </div>
        }
      >
        <AddUser
          values={addForm}
          roleOptions={addRoleOptions}
          establishmentOptions={addEstablishmentOptions}
          onChange={onAddFormChange}
          errors={addUserErrors}
        />
      </Modal>

      <Modal t={t}
        isOpen={isEditModalOpen}
        onClose={closeModal}
        title={t("manageUsers.modal.assignRole")}
        footer={
          <div className={styles.modalFooter}>
            <Button
              label={t("commons.buttons.cancel")}
              variant="secondary"
              onClick={closeModal}
            />
            <Button
              label={t("commons.buttons.submit")}
              onClick={onSubmitEdit}
              disabled={modalLoading}
            />
          </div>
        }
      >
        <PreviewUser
          user={selectedUser}
          roles={editRoles}
          draftRole={draftRole}
          roleOptions={editRoleOptions}
          establishmentOptions={editEstablishmentOptions}
          onRoleUpdate={onRoleUpdate}
          onRoleRemove={onRoleRemove}
          onDraftRoleChange={onDraftRoleChange}
          onDraftRoleAdd={onDraftRoleAdd}
        />
      </Modal>

      <Modal t={t}
        isOpen={isRelaunchModalOpen}
        onClose={closeModal}
        title={t("manageUsers.modal.relaunchUser")}
        footer={
          <div className={styles.modalFooter}>
            <Button
              label={t("commons.buttons.cancel")}
              variant="secondary"
              onClick={closeModal}
            />
            <Button
              label={t("commons.buttons.submit")}
              onClick={onSubmitRelaunch}
              disabled={modalLoading}
            />
          </div>
        }
      >
        <RelaunchUser user={selectedUser} />
      </Modal>
    </div>
  );
};

export default ManageUsers;
